import React from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  TableColumn,
  Link as MuiLink,
  ScreenReaderOnly,
} from '@ally/metronome-ui'
import { Cell, Row } from 'react-table'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import { Account, AccountType } from '@ally/data-types-afg-accounts'
import { formatBalance, getDashDash, isAccountClosed } from '../../utils'
import { AccountDetailsLink, TextBodyOverride } from '../Shared'
import { DashboardView } from '../../CustomizeDashboard/types'
import { STALE_DATA_COPY } from '../../constants'

interface GetColumnsProps {
  tableType: 'self-directed' | 'robo'
  allClosedAccounts: boolean
  tableTypeHeader: string
  view: DashboardView
  isLgDown: boolean
  hasStaleData: boolean
}

interface CellProps {
  cell: Cell<Account>
  row: Row<Account>
}

interface FooterProps {
  rows: Row<Account>[]
}

export const getColumns = ({
  tableType,
  allClosedAccounts,
  tableTypeHeader,
  view,
  isLgDown,
  hasStaleData,
}: GetColumnsProps): TableColumn<Account>[] => {
  const isSelfDirectedTable = tableType === 'self-directed'
  const isDefaultView = view === DashboardView.DEFAULT || isLgDown
  const closedText = (
    <TextBodyOverride tag="span" size="sm" color="error">
      CLOSED
    </TextBodyOverride>
  )

  if (allClosedAccounts) {
    const columns = {
      accountNickname: {
        Header: tableTypeHeader,
        accessor: 'nickName',
        alignment: 'left',
        isSortable: true,
        Cell: ({ cell, row }: CellProps): React.ReactNode | string => {
          return (
            <>
              <AccountDetailsLink
                account={row.original}
                text={cell.value}
                allytm={
                  isSelfDirectedTable
                    ? 'selfDirectedInvestAccount'
                    : 'roboPortfolioInvestAccount'
                }
                hasStaleData={hasStaleData}
              />
              <Box display="inline-block" aria-hidden="true">
                {maskAccountNumber(row.original.accountNumber)}
              </Box>
            </>
          )
        },
      },
      accountStatus: {
        Header: <ScreenReaderOnly>Account Status</ScreenReaderOnly>,
        accessor: 'status',
        alignment: isDefaultView ? 'left' : 'right',
        isSortable: true,
        Cell: (): React.ReactNode | string => closedText,
      },
      empty: {
        Header: '',
        accessor: 'empty',
      },
      empty2: {
        Header: '',
        accessor: 'empty2',
      },
    }
    return isDefaultView
      ? ([columns.accountNickname, columns.accountStatus] as TableColumn<
          Account
        >[])
      : ([
          columns.accountNickname,
          columns.accountStatus,
          columns.empty,
          columns.empty2,
        ] as TableColumn<Account>[])
  }

  const columns = {
    accountNickname: {
      Header: tableTypeHeader,
      Footer: 'Total',
      accessor: 'nickName',
      alignment: 'left',
      isSortable: true,
      Cell: ({ cell, row }: CellProps): React.ReactNode | string => {
        return (
          <>
            <AccountDetailsLink
              account={row.original}
              text={cell.value}
              allytm={
                isSelfDirectedTable
                  ? 'selfDirectedInvestAccount'
                  : 'roboPortfolioInvestAccount'
              }
              hasStaleData={hasStaleData}
            />
            <Box display="inline-block">
              {maskAccountNumber(row.original.accountNumber)}
            </Box>
          </>
        )
      },
    },
    accountValueTotal: {
      Header: 'Account Value',
      accessor: 'value.total',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { value }, row }: CellProps): React.ReactNode | string => {
        if (hasStaleData) {
          return getDashDash(STALE_DATA_COPY)
        }
        const accountClosed = isAccountClosed(row.original) || ''
        if (!accountClosed && value === 0) {
          return (
            <MuiLink text="Add money" allytmln={`${tableType}-add-money`}>
              <Link to="/ext-invest-live/transfer" />
            </MuiLink>
          )
        }
        if (accountClosed) {
          return closedText
        }
        return formatBalance(value)
      },
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () =>
            rows.reduce(
              (sum, row) => (row.values['value.total'] ?? 0) + sum,
              0,
            ),
          [rows],
        )

        return hasStaleData ? getDashDash(STALE_DATA_COPY) : asCurrency(total)
      },
    },
    marketValueTotal: {
      Header: 'Total Securities',
      accessor: 'value.market',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { value }, row }: CellProps): React.ReactNode | string => {
        if (!isAccountClosed(row.original)) {
          if (hasStaleData) {
            return getDashDash(STALE_DATA_COPY)
          }
          return formatBalance(value)
        }
        return ''
      },
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () =>
            rows.reduce(
              (sum, row) => (row.values['value.market'] ?? 0) + sum,
              0,
            ),
          [rows],
        )

        return hasStaleData ? getDashDash(STALE_DATA_COPY) : asCurrency(total)
      },
    },
    cashMoneyMarketTotal: {
      Header: 'Cash',
      accessor: 'value.cash',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { value }, row }: CellProps): React.ReactNode | string => {
        if (!isAccountClosed(row.original)) {
          if (hasStaleData) {
            return getDashDash(STALE_DATA_COPY)
          }
          return asCurrency(value)
        }
        return ''
      },
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () =>
            rows.reduce((sum, row) => (row.values['value.cash'] ?? 0) + sum, 0),
          [rows],
        )

        return hasStaleData ? getDashDash(STALE_DATA_COPY) : asCurrency(total)
      },
    },
    actions: {
      Header: 'Actions',
      accessor: 'actions',
      alignment: 'right',
      isSortable: true,
      Cell: ({ row }: CellProps): React.ReactNode | string => {
        const isSelfDirected = row.original.type === AccountType.INVS
        const selfDirectedRoute = `/ext-invest-live/trading-full/stocks?account=${row.original.id}`
        const roboRoute = `/ext-invest-live/dashboard/${row.original.id}`
        const getRoute = (): string => {
          if (hasStaleData) {
            return `/error`
          }
          return isSelfDirected ? selfDirectedRoute : roboRoute
        }
        if (!isAccountClosed(row.original))
          return (
            <MuiLink
              text={isSelfDirected ? 'Quick Trade' : 'Overview'}
              allytmln={isSelfDirected ? 'quickTrade' : 'overview'}
            >
              <Link to={getRoute()} />
            </MuiLink>
          )
        return ''
      },
    },
  }

  const tileViewCols = [
    columns.accountNickname,
    columns.accountValueTotal,
    columns.marketValueTotal,
    columns.actions,
  ] as TableColumn<Account>[]
  const defaultViewCols = [
    columns.accountNickname,
    columns.accountValueTotal,
    columns.marketValueTotal,
    columns.cashMoneyMarketTotal,
    columns.actions,
  ] as TableColumn<Account>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
